import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useSEO } from '../../hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import { Bell, Plus, Trash2, Pencil, X, ToggleLeft, ToggleRight, Mail, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => v ? new Intl.NumberFormat('fr-DZ').format(v) : '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('dardzair_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const CITIES = ['Alger', 'Oran', 'Béjaïa', 'Tizi Ouzou', 'Annaba'];
const TYPES = [
  { v: '', l: 'Tous types', la: 'جميع الأنواع' },
  { v: 'appartement', l: 'Appartement', la: 'شقة' },
  { v: 'maison', l: 'Maison', la: 'منزل' },
  { v: 'villa', l: 'Villa', la: 'فيلا' },
  { v: 'terrain', l: 'Terrain', la: 'أرض' },
  { v: 'commercial', l: 'Local commercial', la: 'محل تجاري' },
];

export default function AlertsPage() {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isFr = language === 'fr';
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', city: 'Alger', transaction_type: '', property_type: '', min_price: '', max_price: '', min_surface: '', bedrooms: '' });

  useSEO({ title: isFr ? 'Mes alertes - Beytam' : 'تنبيهاتي - بيتم' });

  const loadAlerts = async () => {
    try {
      const res = await fetch(`${API}/api/alerts`, { headers: getAuthHeaders() });
      if (res.ok) setAlerts(await res.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    loadAlerts();
  }, [isAuthenticated]);

  useEffect(() => { loadAlerts(); }, []);

  const resetForm = () => {
    setForm({ name: '', city: 'Alger', transaction_type: '', property_type: '', min_price: '', max_price: '', min_surface: '', bedrooms: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      min_price: form.min_price ? parseFloat(form.min_price) : null,
      max_price: form.max_price ? parseFloat(form.max_price) : null,
      min_surface: form.min_surface ? parseFloat(form.min_surface) : null,
      bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
    };

    try {
      const url = editingId ? `${API}/api/alerts/${editingId}` : `${API}/api/alerts`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (res.ok) {
        toast.success(isFr ? (editingId ? 'Alerte modifiée' : 'Alerte créée !') : (editingId ? 'تم تعديل التنبيه' : 'تم إنشاء التنبيه!'));
        resetForm();
        loadAlerts();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleEdit = (alert) => {
    setForm({
      name: alert.name || '', city: alert.city, transaction_type: alert.transaction_type || '',
      property_type: alert.property_type || '', min_price: alert.min_price || '',
      max_price: alert.max_price || '', min_surface: alert.min_surface || '', bedrooms: alert.bedrooms || '',
    });
    setEditingId(alert.id);
    setShowForm(true);
  };

  const handleToggle = async (alert) => {
    try {
      await fetch(`${API}/api/alerts/${alert.id}`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ ...alert, active: !alert.active }),
      });
      loadAlerts();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isFr ? 'Supprimer cette alerte ?' : 'حذف هذا التنبيه؟')) return;
    try {
      await fetch(`${API}/api/alerts/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      toast.success(isFr ? 'Alerte supprimée' : 'تم حذف التنبيه');
      loadAlerts();
    } catch {}
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="alerts-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2" data-testid="alerts-title">
              <Bell className="w-7 h-7 text-blue-900" />
              {isFr ? 'Mes alertes immobilières' : 'تنبيهاتي العقارية'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {isFr ? 'Recevez un email dès qu\'un bien correspond à vos critères.' : 'استلم بريداً عند ظهور عقار يطابق معاييرك.'}
            </p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-5 py-3 rounded-xl font-semibold text-sm transition-colors shrink-0"
            data-testid="create-alert-btn">
            <Plus className="w-4 h-4" /> {isFr ? 'Nouvelle alerte' : 'تنبيه جديد'}
          </button>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6" data-testid="alerts-info">
          <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            {isFr
              ? 'Quand une nouvelle annonce est publiée et correspond à vos critères, vous recevez un email automatiquement avec les détails du bien et un lien direct.'
              : 'عندما يتم نشر إعلان جديد يتوافق مع معاييرك، ستتلقى بريداً إلكترونياً تلقائياً مع تفاصيل العقار ورابط مباشر.'}
          </p>
        </div>

        {/* Create/Edit form modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="alert-form-modal">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
              <button onClick={resetForm} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              <h2 className="text-xl font-bold text-slate-800 mb-5">{editingId ? (isFr ? 'Modifier l\'alerte' : 'تعديل التنبيه') : (isFr ? 'Créer une alerte' : 'إنشاء تنبيه')}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Nom de l\'alerte' : 'اسم التنبيه'}</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={isFr ? 'Ex: Appartement Alger centre' : 'مثال: شقة وسط الجزائر'}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="alert-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Ville' : 'المدينة'}</label>
                    <select value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-city">
                      {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Transaction' : 'نوع المعاملة'}</label>
                    <select value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-transaction">
                      <option value="">{isFr ? 'Toutes' : 'الكل'}</option>
                      <option value="vente">{isFr ? 'Vente' : 'بيع'}</option>
                      <option value="location">{isFr ? 'Location' : 'إيجار'}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Type de bien' : 'نوع العقار'}</label>
                  <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-type">
                    {TYPES.map(t => <option key={t.v} value={t.v}>{isFr ? t.l : t.la}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Prix min (DA)' : 'السعر الأدنى (دج)'}</label>
                    <input type="number" value={form.min_price} onChange={e => setForm({ ...form, min_price: e.target.value })} placeholder="0"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-min-price" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Prix max (DA)' : 'السعر الأقصى (دج)'}</label>
                    <input type="number" value={form.max_price} onChange={e => setForm({ ...form, max_price: e.target.value })} placeholder="50 000 000"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-max-price" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Surface min (m²)' : 'المساحة الأدنى (م²)'}</label>
                    <input type="number" value={form.min_surface} onChange={e => setForm({ ...form, min_surface: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-min-surface" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">{isFr ? 'Chambres min' : 'عدد الغرف الأدنى'}</label>
                    <input type="number" value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="alert-bedrooms" />
                  </div>
                </div>
                <button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-3 rounded-xl transition-colors" data-testid="alert-submit">
                  {editingId ? (isFr ? 'Enregistrer les modifications' : 'حفظ التعديلات') : (isFr ? 'Créer l\'alerte' : 'إنشاء التنبيه')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Alerts list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">{isFr ? 'Chargement...' : 'جاري التحميل...'}</div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-2 font-medium">{isFr ? 'Aucune alerte configurée' : 'لا توجد تنبيهات'}</p>
            <p className="text-slate-400 text-sm mb-4">{isFr ? 'Créez votre première alerte pour ne rien manquer.' : 'أنشئ أول تنبيه حتى لا يفوتك شيء.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map(alert => (
              <div key={alert.id} className={`bg-white rounded-xl border ${alert.active ? 'border-slate-100' : 'border-slate-200 opacity-60'} shadow-sm p-5`} data-testid={`alert-${alert.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="font-bold text-slate-800 truncate">{alert.name}</h3>
                      {alert.active ? (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{isFr ? 'Active' : 'نشط'}</span>
                      ) : (
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{isFr ? 'Désactivée' : 'معطل'}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                      <span className="bg-slate-50 px-2 py-0.5 rounded">{alert.city}</span>
                      {alert.transaction_type && <span className="bg-slate-50 px-2 py-0.5 rounded">{alert.transaction_type === 'vente' ? (isFr ? 'Vente' : 'بيع') : (isFr ? 'Location' : 'إيجار')}</span>}
                      {alert.property_type && <span className="bg-slate-50 px-2 py-0.5 rounded">{alert.property_type}</span>}
                      {alert.min_price && <span className="bg-slate-50 px-2 py-0.5 rounded">{isFr ? 'Min' : 'أدنى'}: {fmt(alert.min_price)} DA</span>}
                      {alert.max_price && <span className="bg-slate-50 px-2 py-0.5 rounded">{isFr ? 'Max' : 'أقصى'}: {fmt(alert.max_price)} DA</span>}
                      {alert.min_surface && <span className="bg-slate-50 px-2 py-0.5 rounded">≥ {alert.min_surface} m²</span>}
                      {alert.bedrooms && <span className="bg-slate-50 px-2 py-0.5 rounded">≥ {alert.bedrooms} {isFr ? 'ch.' : 'غرف'}</span>}
                    </div>
                    {alert.matches_sent > 0 && (
                      <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {alert.matches_sent} {isFr ? 'correspondances envoyées' : 'مطابقات مرسلة'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => handleToggle(alert)} className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-slate-50" data-testid={`toggle-${alert.id}`}>
                      {alert.active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => handleEdit(alert)} className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-slate-50" data-testid={`edit-${alert.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(alert.id)} className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50" data-testid={`delete-alert-${alert.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
